import React, { useEffect, useState, useRef } from "react";
import { filterChars, filterNumber, filterPrice, filterDecimal, kbdEvent, filterAlpha } from "../utils/Utils";
import { showError } from "./Message";
import { KBD } from "../utils/Keybindings";
import { SearchIcon } from "../utils/Icons";
import ToolTip from "./ToolTip";
import NumPicker from "./NumPicker";

const Input = React.forwardRef((props, ref) => {
    const {
        value = "",
        className = "",
        secondary = false,
        warning = false,
        black = false,
        style = {},
        right = false,
        medium,
        edit = false,
        placeholder = "",
        onEnter = null,
        onPageUp = null,
        onPageDown = null,
        setHelp = () => { },
        onMouseDown = () => { },
        onChange = () => { },
        onClick = () => { },
        onFocus = () => { },
        onBlur = () => { }, // Add onBlur for handling focus loss
        onClear = null,
        onBackSpace = null,
        controlled = false,
        render = false, // Custom render value to show when not focused
        autoselect = false,
        price = false,
        decimal = false,
        number = false,
        alpha = false,
        doubleclick = false,
        limit = false,
        uppercase = false,
        lowercase = false,
        validator = null, // New prop for regex validation
        valFunction = false,
        validateOnLoad = false,
        minLength = 0,
        maxLength = 0,
        cell = false,
        border = false,
        double = false,
        chars = [],
        custom = false,
        range = false,
        number_default = 0,
        autofocus = false,
        parse = false,
        notilde = false,
        password = false,
        type = "text",
        grow = false,
        shrink = true,
        restrict = false,
        restrictMsg = "",
        opened = false,
        tabMax = false,
        tabMin = false,
        help = false,
        onFill = false,
        onSearch = false,
        dataId = "",
        numpicker = false,
        icon = <SearchIcon />,
        feedback = false
    } = props;


    const width = (onSearch) ? props.width - 40 : props.width;
    const validateRef = useRef(0);
    // State to track focus
    const [isFocused, setIsFocused] = useState(false);
    const [isValid, setIsValid] = useState(true);
    // const [insert, setInsert] = useState(props?.insert || true);
    // const inputRef = useRef(null);
    // const mergedRef = (instance) => {
    //     inputRef.current = instance;
    //     if (ref) ref.current = instance;
    // };
    // Define base classes
    let _class = edit ? "ui-input edit" : "ui-input";

    // if (!insert) _class += " overwrite"; 

    if (cell) {
        _class = (edit) ? "ui-cell-edit" : "ui-cell";
        if (border) { _class += " border"; }
        if (double) { _class += " double"; }
        if (width) { style.minWidth = width + "px"; }
    }

    if (secondary) _class += " secondary";
    if (warning) _class += " warning";
    if (className) _class += ` ${className}`;
    if (right) _class += " ra";
    if (width && !cell) style.width = `${width}px`;
    if (medium) _class += " medium";
    if (black) _class += " black";
    if (!isValid && edit) _class += " error"; // Add 'error' class if not valid

    let _search_btn = "ui-input-search-btn"
    if (onSearch) {
        _class += " search"

        if (isFocused) {
            _search_btn += " edit";
        }
    };

    if (grow) {
        style.flexGrow = 1;
        style.width = width;
    }

    if (!shrink) {
        style.flexShrink = 0;
    }

    if (opened) {
        _class = _class + " select";
    }

    const runValidation = (_v) => {

        if (!valFunction) {
            return;
        }

        const result = valFunction(_v);

        if (result instanceof Promise) {
            const id = ++validateRef.current;

            setIsValid(null); // loading state

            result.then((res) => {
                if (id !== validateRef.current) return;
                if (restrict && !res) {
                    if (restrictMsg) {
                        showError({ error: restrictMsg });
                    }
                    return;
                }

                setIsValid(res);
            });

        } else {
            if (restrict) {
                if (!result) {
                    if (restrictMsg) {
                        showError({ error: restrictMsg });
                    }
                    return;
                }
            } else {
                setIsValid(result);
            }
        }

    }


    useEffect(() => {
        if (value && validateOnLoad) {
            runValidation(value);
        }
    }, []);


    // Handle value changes (e.g., price filtering, limiting length, etc.)
    const change = (_v) => {

        const prev = value ?? "";

        if (custom) _v = custom(_v);
        if (price) _v = filterPrice(_v);
        if (number) _v = filterNumber(_v, number_default);
        if (alpha) _v = filterAlpha(_v);
        if (limit) _v = _v.slice(0, limit);
        if (uppercase) _v = _v.toUpperCase();
        if (lowercase) _v = _v.toLowerCase();
        if (chars.length > 0) { _v = filterChars(_v, chars) }
        if (validator) {

            if (restrict) {

                alert(_v)
                if (!validator.test(_v)) {

                    return;
                }
            } else {
                setIsValid(validator.test(_v));
            }
            runValidation(_v)
        }






        if (parse) { _v = parse(_v); }
        if (decimal) _v = filterDecimal(_v);
        if (notilde) {
            _v = _v.replace("~", "");
        }

        if (onFill && maxLength === _v.length) {
            onFill(_v);
        }

        if (tabMax !== false && maxLength === _v.length) {
            tabMax();
        }
        if (tabMin !== false && _v.length === 0 && value?.length === 0) {
            tabMin();
        }

        if (onClear && prev !== "" && _v === "") {
            onClear();
        }

        onChange(_v);
    };

    // Handle double click event
    const handleDoubleClick = (e) => {
        if (typeof doubleclick === 'function') {
            doubleclick(e);
        } else {
            kbdEvent(doubleclick);
        }

    };

    // Handle focus and blur
    const handleFocus = (e) => {
        setIsFocused(true);
        if (autoselect) {
            const input = e.target;
            const selectionLength = input.selectionEnd - input.selectionStart;
            if (selectionLength === 0 && input.value.length > 0) {
                requestAnimationFrame(() => input.select());
            }
        }

        if (help !== false) {
            setHelp(help);
        }
        onFocus();
    };

    useEffect(() => {
        if (edit) {
            if (validator) { setIsValid(validator.test(value)); }

            if (valFunction) {
                setIsValid(valFunction(value));
            }
        }

    }, [edit, value])

    const handleBlur = (e) => {
        setIsFocused(false);
        onBlur(e.target.value);
    };

    const formatPriceDisplay = (val) => {
        if (val === null || val === "") return "";
        const num = parseFloat(val);
        if (isNaN(num)) return val;
        return num.toFixed(2);
    };


    // Render the input field if edit mode is on and the input is focused
    if (edit) {


        const inputEl = (
            <>
             {(feedback && <div class="ui-input-msg">{feedback}</div>)}
                <input
                    data-id={dataId}
                    type={(password) ? "password" : type}
                    {...(minLength && { minLength })}
                    {...(maxLength && { maxLength })}
                    autoFocus={autofocus}
                    onDoubleClick={doubleclick ? handleDoubleClick : undefined}
                    spellCheck={false}
                    onKeyDown={(e) => {

                        if(e.key === "y" && e?.ctrlKey){
                            change("");
                        }

                        if (e.key && e.key === value) {
                            onChange(value);
                        }

                        if (e.key === "Backspace") {
                            if (onBackSpace) { if (e?.shiftKey) { return; } onBackSpace(e) }
                        }

                        if (e.key === "Enter") {
                            if (onEnter) { if (e?.shiftKey) { return; } onEnter(e.target.value, e); e.target.value = "" }
                        }
                        if (e.key === "PageUp") {
                            if (onPageUp) { onPageUp(e.target.value); e.target.value = "" }
                        }

                        if (e.key === "PageDown") {
                            if (onPageDown) { onPageDown(e.target.value, e); e.target.value = "" }
                        }

                    }}
                    autoComplete="off"
                    onChange={(e) => change(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur} // Handle focus loss
                    // ref={mergedRef}
                    ref={ref}
                    placeholder={placeholder}
                    style={{ ...style }}
                    className={_class}
                    {...(controlled
                        ? {
                            value: isFocused
                                ? (value !== null ? value : '')
                                : (render ? render : (price ? formatPriceDisplay(value) : (value !== null ? value : '')))
                        }
                        : {
                            defaultValue: isFocused
                                ? (value !== null ? value : '')
                                : (render ? render : (price ? formatPriceDisplay(value) : (value !== null ? value : '')))
                        }
                    )}
                />
                
            </>
        );

        const drawInput = () => {

            if (numpicker) {
                return (<NumPicker>{inputEl}</NumPicker>)
            } else {
                return inputEl;
            }

        }

        if (!onSearch) return drawInput();

        return (
            <div data-id={dataId} style={{ flexGrow: (grow) ? "1" : "0" }} className="ui-input-search-wrapper">
                {drawInput()}
                <button
                    type="button"
                    className={_search_btn}
                    aria-label="Search"
                    onMouseDown={(e) => { e.preventDefault() }} // prevents blur
                    onClick={() => {
                        if (isFocused) {
                            onSearch(value)
                        }
                    }}
                >
                    {icon}
                </button>
            </div>
        );

    }

    // When not in edit mode, show custom render content or value based on focus
    return (
        <div data-id={dataId} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onMouseDown() }} onClick={() => onClick()} style={{ ...style }} className={_class}>
            {(render !== false)
                ? render
                : (!value && placeholder)
                    ? <em style={{ "opacity": "0.5", "pointerEvents": "none" }}>{placeholder}</em>
                    : (price ? formatPriceDisplay(value) : value)
            }
        </div>
    );
});

export default Input;
